import React from "react";
import "./sponsor.css";
import {
  Box,
  Card,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";

const SponsorType = Object.freeze({
  GOLD: "gold",
  SILVER: "silver",
  BRONZE: "bronze",
  GUEST: "guest",
});

const sponsors = [
  {
    name: "University of Waterloo Faculty of Engineering",
    logoPath: "/images/sponsors/gold/waterloo_engineering_logo_rgb.png",
    type: SponsorType.GOLD,
    text: "https://uwaterloo.ca/engineering/",
    size: "big",
  },
  {
    name: "EngSoc",
    logoPath: "/images/sponsors/gold/engsoc.png",
    type: SponsorType.GOLD,
    text: "https://www.engsoc.uwaterloo.ca/",
    size: "medium",
  },
  {
    name: "IMHOTEP ENGINEERING INC.",
    logoPath: "/images/sponsors/gold/IMHOTEP.png",
    type: SponsorType.GOLD,
    text: "https://www.imhotepengineering.ca/",
    size: "big",
  },
  {
    name: "Mel's Diner",
    logoPath: "/images/sponsors/gold/mels.png",
    type: SponsorType.GOLD,
    text: "https://www.melsdiner.ca/",
    size: "big",
  },
  {
    name: "Waterloo Engineering Endowment Fund (WEEF)",
    logoPath: "/images/sponsors/gold/weef.png",
    type: SponsorType.GOLD,
    text: "https://uwaterloo.ca/engineering-endowment-foundation/",
    size: "big",
  },
  {
    name: "Waterloo Undergraduate Student Association (WUSA)",
    logoPath: "/images/sponsors/gold/wusa.png",
    type: SponsorType.GOLD,
    text: "https://wusa.ca/",
    size: "big",
  },
  {
    name: "J.L. Richards & Associates Limited",
    logoPath: "/images/sponsors/bronze/JLRichards.jpg",
    type: SponsorType.BRONZE,
    text: "https://www.jlrichards.ca/",
    size: "big",
  },
  {
    name: "Professional Engineers Ontario",
    logoPath: "/images/sponsors/bronze/peo.png",
    type: SponsorType.BRONZE,
    text: "https://www.peo.on.ca/",
    size: "big",
  },
];

const divider = () => {
  return (
    <Grid item xs="12">
      <Divider sx={{ backgroundColor: "engPrimary.main" }} />
    </Grid>
  );
};

const SponsorTableSection = ({ sponsors, header }) => {
  const [sponsorsTooltip, setSponsorsTooltip] = React.useState("");

  const handleTooltipOpen = (sponsor) => () => {
    setSponsorsTooltip(sponsor);
  };

  const handleTooltipClose = (sponsor) => () => {
    setSponsorsTooltip((sponsorsTooltip) =>
      sponsorsTooltip === sponsor ? "" : sponsorsTooltip,
    );
  };

  return (
    <Box
      display="block"
      sx={{ width: "100%", maxWidth: "1000px", padding: "0", mt: 4 }}
    >
      {header}
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ pt: "1rem", pb: "2rem" }}
      >
        {sponsors.map((sponsor) => {
          let size_dict;
          switch (sponsor.size) {
            case "huge":
              size_dict = { xs: 12, md: 6, lg: 4 };
              break;
            case "big":
              size_dict = { xs: 6, md: 4, lg: 3 };
              break;
            case "medium":
              size_dict = { xs: 4, md: 3, lg: 2 };
              break;
            case "small":
            default:
              size_dict = { xs: 3, md: 2, lg: 1 };
              break;
          }
          return (
            <Grid item {...size_dict} key={sponsor.name}>
              <Box className="logo-container">
                <Card elevation={0} className="responsive-logo">
                  <Tooltip
                    title={sponsor.name}
                    enterDelay={500}
                    leaveDelay={200}
                    disableTouchListener
                    TransitionComponent={Zoom}
                    open={sponsorsTooltip === sponsor.name}
                    onOpen={handleTooltipOpen(sponsor.name)}
                    onClose={handleTooltipClose(sponsor.name)}
                  >
                    <img
                      src={sponsor.logoPath}
                      alt={sponsor.name}
                      style={{
                        objectFit: "contain",
                        display: "block",
                        height: "100%",
                        width: "100%",
                      }}
                      onClick={() => {
                        if (sponsor.text) {
                          window.open(
                            sponsor.text,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        } else {
                          handleTooltipOpen(sponsor.name)();
                          setTimeout(handleTooltipClose(sponsor.name), 3000);
                        }
                      }}
                    />
                  </Tooltip>
                </Card>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const SponsorTable = () => {
  const goldSponsors = sponsors.filter((s) => s.type === SponsorType.GOLD);
  const silverSponsors = sponsors.filter((s) => s.type === SponsorType.SILVER);
  const bronzeSponsors = sponsors.filter((s) => s.type === SponsorType.BRONZE);
  const guestSponsors = sponsors.filter((s) => s.type === SponsorType.GUEST);

  return (
    <Stack sx={{ padding: "2em" }}>
      <Box display="flex" justifyContent="center">
        <Typography variant="h2" align="center">
          Our Lovely Sponsors
        </Typography>
      </Box>
      <SponsorTableSection
        sponsors={goldSponsors}
        header={
          <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
            <Typography
              variant="h3"
              align="center"
              color="gold"
              sx={{ textShadow: "1px 1px 2px rgba(0,0,0,0.5)" }}
            >
              Gold Sponsors
            </Typography>
          </Box>
        }
      />
      <SponsorTableSection
        sponsors={silverSponsors}
        header={
          <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
            <Typography
              variant="h3"
              align="center"
              color="silver"
              sx={{ textShadow: "1px 1px 2px rgba(0,0,0,0.5)" }}
            >
              Silver Sponsors
            </Typography>
          </Box>
        }
      />
      <SponsorTableSection
        sponsors={bronzeSponsors}
        header={
          <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
            <Typography
              variant="h3"
              align="center"
              color="peru"
              sx={{ textShadow: "1px 1px 2px rgba(0,0,0,0.5)" }}
            >
              Bronze Sponsors
            </Typography>
          </Box>
        }
      />
      <SponsorTableSection
        sponsors={guestSponsors}
        header={
          <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
            <Typography
              variant="h3"
              align="center"
              color="text.primary"
              sx={{ textShadow: "1px 1px 2px rgba(0,0,0,0.5)" }}
            >
              Special Thanks to Our Guests
            </Typography>
          </Box>
        }
      />
    </Stack>
  );
};

export default SponsorTable;
